import { Card, Progress, Tag, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { PartnerOnboardingStatuses, partnerTypeLabel } from './constants';
import ViewPartnerOnboardingModal from './ViewPartnerOnboardingModal';
import { useMutation } from '@apollo/client';
import { deletePartnerOnboardingMutation } from './constants';

const { Text } = Typography;

const STATUS_KEYS = ['IN_PROGRESS', 'COMPLETE', 'CANCELLED'];

function stageColor(colorName) {
  const map = {
    blue: '#1677ff',
    orange: '#fa8c16',
    purple: '#722ed1',
    cyan: '#13c2c2',
    green: '#52c41a',
    red: '#ff4d4f',
  };
  return map[colorName] || '#d9d9d9';
}

function PartnerCard({ record, onClick }) {
  const done = record.tasksDoneCount ?? 0;
  const total = record.tasksTotalCount ?? 0;
  const regionLabel = record.regionName || record.region;
  const contactName = record.contact?.fullName;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(record._id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(record._id);
        }
      }}
      style={{
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderLeft: `4px solid ${stageColor(PartnerOnboardingStatuses[record.status]?.color)}`,
        borderRadius: 6,
        padding: '10px 12px',
        marginBottom: 8,
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>
        {contactName || (
          <Text type="secondary" italic style={{ fontWeight: 400 }}>
            Unknown contact
          </Text>
        )}
      </div>
      <div style={{ marginBottom: 6 }}>
        {record.partnerType && (
          <Tag style={{ fontSize: 11, marginRight: 4 }}>{partnerTypeLabel(record.partnerType)}</Tag>
        )}
        <Tag style={{ fontSize: 11 }} color="default">{regionLabel}</Tag>
      </div>
      {total > 0 && (
        <Progress
          percent={Math.round((done / total) * 100)}
          size="small"
          format={() => `${done}/${total}`}
          status={done === total ? 'success' : 'normal'}
        />
      )}
    </div>
  );
}

function KanbanView({ partnerOnboardings, refetch, statusFilter }) {
  const [viewingId, setViewingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteOnboarding] = useMutation(deletePartnerOnboardingMutation);
  // The query only returns the filtered status, so showing the other columns would
  // promise partners the board can never hold.
  const columnKeys =
    statusFilter && STATUS_KEYS.includes(statusFilter)
      ? [statusFilter]
      : STATUS_KEYS;

  const grouped = useMemo(() => {
    const map = {};
    STATUS_KEYS.forEach((s) => { map[s] = []; });
    (partnerOnboardings || []).forEach((p) => {
      const status = p.status || 'IN_PROGRESS';
      if (!map[status]) map[status] = [];
      map[status].push(p);
    });
    return map;
  }, [partnerOnboardings]);

  const handleDelete = async (_id) => {
    setDeleting(_id);
    try {
      await deleteOnboarding({ variables: { _id } });
      refetch();
    } catch (e) {
      // handled by modal
    }
    setDeleting(null);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 16 }}>
        {columnKeys.map((statusKey) => {
          const status = PartnerOnboardingStatuses[statusKey];
          const cards = grouped[statusKey] || [];
          return (
            <div key={statusKey} style={{ minWidth: 220, flex: '0 0 220px' }}>
              <Card
                size="small"
                title={
                  <span>
                    <Tag color={status?.color} style={{ marginRight: 6 }}>{status?.label}</Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>{cards.length}</Text>
                  </span>
                }
                style={{ borderTop: `3px solid ${stageColor(status?.color)}` }}
                bodyStyle={{ padding: '8px 8px 0', maxHeight: '70vh', overflowY: 'auto' }}
              >
                {cards.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', padding: '8px 0' }}>
                    No partners
                  </Text>
                ) : (
                  cards.map((p) => (
                    <PartnerCard key={p._id} record={p} onClick={setViewingId} />
                  ))
                )}
              </Card>
            </div>
          );
        })}
      </div>
      <ViewPartnerOnboardingModal
        _id={viewingId}
        visible={!!viewingId}
        onCancel={() => setViewingId(null)}
        onSuccess={() => {
          setViewingId(null);
          refetch();
        }}
        onDelete={handleDelete}
        deleting={deleting}
      />
    </>
  );
}

export default KanbanView;
